import React, { useState, useCallback } from 'react';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from './ui/drawer';
import { Tag, Plus, Check, FolderPlus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

/**
 * FavoriteListsPopover - Popover pour assigner un favori à plusieurs listes
 * 
 * Props:
 * @param {string} favoriteId - ID du favori
 * @param {Array} currentListIds - IDs des listes auxquelles le favori appartient actuellement
 * @param {Array} allLists - Toutes les listes disponibles pour l'utilisateur
 * @param {Function} onListsChange - Callback après modification des associations
 * @param {boolean} isMobile - Afficher en mode mobile (drawer)
 */
const FavoriteListsPopover = ({ 
  favoriteId, 
  currentListIds = [], 
  allLists = [], 
  onListsChange,
  isMobile = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedLists, setSelectedLists] = useState(new Set(currentListIds));
  const [saving, setSaving] = useState(false);

  // Sync selectedLists when props change
  React.useEffect(() => {
    setSelectedLists(new Set(currentListIds));
  }, [currentListIds]);

  // Toggle une liste
  const handleToggleList = useCallback((listId) => {
    setSelectedLists(prev => {
      const newSet = new Set(prev);
      if (newSet.has(listId)) {
        newSet.delete(listId);
      } else {
        newSet.add(listId);
      }
      return newSet;
    });
  }, []);

  // Sauvegarder les modifications
  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const currentSet = new Set(currentListIds);
      const newSet = selectedLists;
      
      // Listes à ajouter
      const toAdd = [...newSet].filter(id => !currentSet.has(id));
      // Listes à retirer
      const toRemove = [...currentSet].filter(id => !newSet.has(id));
      
      // Ajouter aux nouvelles listes
      for (const listId of toAdd) {
        await api.post(`/favorite-lists/${listId}/items`, {
          favorite_ids: [favoriteId]
        });
      }
      
      // Retirer des anciennes listes
      for (const listId of toRemove) {
        await api.delete(`/favorite-lists/${listId}/items/${favoriteId}`);
      }
      
      toast.success('Listes mises à jour');
      
      if (onListsChange) {
        onListsChange(favoriteId, [...newSet]);
      }
      
      setIsOpen(false);
    } catch (error) {
      console.error('Error updating lists:', error);
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  }, [favoriteId, currentListIds, selectedLists, onListsChange]);

  // Contenu du popover/drawer
  const content = (
    <div className="space-y-3">
      {allLists.length === 0 ? (
        <div className="text-center py-4 text-muted-foreground">
          <FolderPlus className="h-8 w-8 mx-auto mb-2 text-gray-300" />
          <p className="text-sm">Aucune liste créée</p>
          <p className="text-xs mt-1">Créez une liste depuis le gestionnaire</p>
        </div>
      ) : (
        <>
          <div className="space-y-2 max-h-[250px] overflow-y-auto">
            {allLists.map(list => {
              const isSelected = selectedLists.has(list.id);
              return (
                <label 
                  key={list.id}
                  className={`
                    flex items-center gap-3 p-2 rounded-md cursor-pointer transition-colors
                    ${isSelected ? 'bg-primary/10 border border-primary/30' : 'hover:bg-gray-50 border border-transparent'}
                  `}
                  data-testid={`list-checkbox-${list.id}`}
                >
                  <Checkbox 
                    checked={isSelected}
                    onCheckedChange={() => handleToggleList(list.id)}
                  />
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <Tag className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                    <span className={`text-sm truncate ${isSelected ? 'font-medium' : ''}`}>
                      {list.name}
                    </span>
                  </div>
                  {isSelected && (
                    <Check className="h-4 w-4 text-primary flex-shrink-0" />
                  )}
                </label>
              );
            })}
          </div>
          
          <div className="pt-2 border-t flex justify-end gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setIsOpen(false)}
              disabled={saving}
            >
              Annuler
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={saving}
              data-testid="save-lists-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  ...
                </>
              ) : (
                <>
                  <Check className="h-3 w-3 mr-1" />
                  Valider
                </>
              )}
            </Button>
          </div>
        </>
      )}
    </div>
  );

  // Bouton trigger avec badges
  const triggerButton = (
    <Button 
      variant="ghost" 
      size="sm" 
      className="h-auto py-1 px-2 flex items-center gap-1 text-muted-foreground hover:text-foreground"
      data-testid={`lists-trigger-${favoriteId}`}
    >
      <Plus className="h-3.5 w-3.5" />
      <span className="text-xs">Listes</span>
    </Button>
  );

  // Version Mobile: Drawer
  if (isMobile) {
    return (
      <Drawer open={isOpen} onOpenChange={setIsOpen}>
        <DrawerTrigger asChild>
          {triggerButton}
        </DrawerTrigger>
        <DrawerContent className="px-4 pb-6">
          <DrawerHeader className="text-left px-0">
            <DrawerTitle className="flex items-center gap-2">
              <Tag className="h-5 w-5" />
              Ajouter aux listes
            </DrawerTitle>
          </DrawerHeader>
          {content}
        </DrawerContent>
      </Drawer>
    );
  }

  // Version Desktop: Popover
  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        {triggerButton}
      </PopoverTrigger>
      <PopoverContent 
        className="w-72 p-3" 
        align="start"
        sideOffset={5}
      >
        <div className="flex items-center gap-2 mb-3 pb-2 border-b">
          <Tag className="h-4 w-4 text-primary" />
          <span className="font-medium text-sm">Ajouter aux listes</span>
        </div>
        {content}
      </PopoverContent>
    </Popover>
  );
};

/**
 * FavoriteListBadges - Affiche les badges des listes associées
 */
export const FavoriteListBadges = ({ listIds = [], allLists = [], maxDisplay = 2 }) => {
  if (!listIds || listIds.length === 0) return null;
  
  // Trouver les noms des listes
  const listNames = listIds
    .map(id => allLists.find(l => l.id === id))
    .filter(Boolean)
    .map(l => l.name);
  
  if (listNames.length === 0) return null;
  
  const displayedNames = listNames.slice(0, maxDisplay);
  const remaining = listNames.length - maxDisplay;
  
  return (
    <div className="flex flex-wrap gap-1">
      {displayedNames.map((name, idx) => (
        <Badge 
          key={idx} 
          variant="secondary" 
          className="text-xs px-1.5 py-0 h-5 bg-primary/10 text-primary border-primary/20"
        >
          {name}
        </Badge>
      ))}
      {remaining > 0 && (
        <Badge 
          variant="outline" 
          className="text-xs px-1.5 py-0 h-5"
        >
          +{remaining}
        </Badge>
      )}
    </div>
  );
};

export default FavoriteListsPopover;
